<template>
  <section class="article-quote-module-component">
    <div class="sa-container">
      <blockquote ref="quoteFrameRef">
        <svg
          class="quote-corner top-right"
          xmlns="http://www.w3.org/2000/svg"
          width="98"
          height="98"
          viewBox="0 0 98 98"
          fill="none"
          aria-hidden="true">
          <rect
            x="4.28372e-06"
            y="47.9425"
            width="47.9424"
            height="98"
            transform="rotate(-90 4.28372e-06 47.9425)"
            fill="#13D377" />
          <rect
            x="98"
            y="98.0001"
            width="47.9424"
            height="98"
            transform="rotate(180 98 98.0001)"
            fill="#13D377" />
        </svg>

        <div class="quote-content">
          <div
            v-if="data.quote"
            ref="quoteTextRef"
            class="quote-text"
            v-html="data.quote"></div>

          <footer
            v-if="attribution.name || attribution.position"
            ref="attributionRef"
            class="attribution">
            <cite
              v-if="attribution.name"
              v-html="attribution.name"></cite>
            <div
              v-if="attribution.position"
              class="position"
              v-html="attribution.position"></div>
          </footer>
        </div>

        <svg
          class="quote-corner bottom-left"
          xmlns="http://www.w3.org/2000/svg"
          width="98"
          height="98"
          viewBox="0 0 98 98"
          fill="none"
          aria-hidden="true">
          <rect
            x="98"
            y="50.0576"
            width="47.9424"
            height="98"
            transform="rotate(90 98 50.0576)"
            fill="#13D377" />
          <rect
            x="2.38262e-05"
            width="47.9424"
            height="98"
            fill="#13D377" />
        </svg>
      </blockquote>
    </div>
  </section>
</template>

<script setup>
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import SplitType from 'split-type'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

gsap.registerPlugin(ScrollTrigger)

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
})

const quoteFrameRef = ref(null)
const quoteTextRef = ref(null)
const attributionRef = ref(null)
const quoteTween = ref(null)
const frameTween = ref(null)
const splitQuote = ref(null)

const attribution = computed(() => props.data?.attribution || {})

onMounted(() => {
  if (quoteTextRef.value) {
    splitQuote.value = new SplitType(quoteTextRef.value.children, {
      types: 'lines, words',
    })

    quoteTween.value = gsap.fromTo(
      quoteTextRef.value.querySelectorAll('.line > .word'),
      { yPercent: 110 },
      {
        yPercent: 0,
        ease: 'power4.out',
        stagger: 0.04,
        scrollTrigger: {
          trigger: quoteTextRef.value,
          start: 'top 85%',
          end: 'bottom 55%',
          scrub: 1,
        },
      }
    )
  }

  const frameElements = [
    attributionRef.value,
    ...quoteFrameRef.value.querySelectorAll('.quote-corner'),
  ].filter(Boolean)

  frameTween.value = gsap.fromTo(
    frameElements,
    { autoAlpha: 0, y: 30 },
    {
      autoAlpha: 1,
      y: 0,
      duration: 0.8,
      stagger: 0.12,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: quoteFrameRef.value,
        start: 'top 85%',
        once: true,
      },
    }
  )
})

onBeforeUnmount(() => {
  quoteTween.value?.scrollTrigger?.kill()
  quoteTween.value?.kill()
  frameTween.value?.scrollTrigger?.kill()
  frameTween.value?.kill()
  splitQuote.value?.revert()
})
</script>

<style lang="scss" scoped>
.article-quote-module-component {
  @apply relative overflow-hidden bg-white py-8 lg:py-10;

  blockquote {
    @apply relative m-0 px-10 py-16 md:px-20 lg:px-[120px] lg:py-[85px];

    .quote-content {
      @apply relative z-2;

      .quote-text {
        @apply text-[32px] leading-[1] tracking-normal text-balanced lg:text-[60px] lg:tracking-[-2px];

        :deep(*) {
          @apply text-inherit;
        }

        :deep(p),
        :deep(h1),
        :deep(h2),
        :deep(h3),
        :deep(h4),
        :deep(h5),
        :deep(h6) {
          @apply m-0 text-[32px] font-normal leading-[1] tracking-normal text-balanced lg:text-[60px] lg:tracking-[-2px];
        }

        :deep(.line) {
          @apply overflow-hidden;
        }

        :deep(.word) {
          @apply text-balanced;
        }
      }

      .attribution {
        @apply relative mt-10 lg:mt-12;

        cite {
          @apply block text-[20px] font-medium not-italic leading-[1.2] text-humble lg:text-[22px];
        }

        .position {
          @apply mt-1 max-w-[480px] text-[16px] leading-[1.25] text-humble;
        }
      }
    }

    .quote-corner {
      @apply pointer-events-none absolute h-[50px] w-[50px] lg:h-[98px] lg:w-[98px];

      &.top-right {
        @apply top-0 right-0;
      }

      &.bottom-left {
        @apply bottom-0 left-0;
      }
    }
  }
}
</style>
